import { useTranslation } from 'react-i18next'
import type { ValueOf } from 'type-fest'
import IcRoundLockIcon from '~icons/ic/round-lock'
import MediaPreview from 'src/components/Common/MediaPreview'
import { sheetModelDetail } from 'src/components/GenerationModel/GenerationModelDetail/ModelDetailDialog'
import ModelLikeButton from 'src/components/GenerationModel/ModelLikeBtn'
import ModelTypeChip, {
  ModelPreviewEAChip,
} from 'src/components/GenerationModel/ModelTypeChip'
import { MediaContentRemovedIcon } from 'src/components/Media/MediaContentRemoved'
import { MembershipIconCircled } from 'src/components/Membership/MembershipIcon'
import type { GenerationModelPreviewFragment } from 'src/services/apollo/generated/graphql'
import { cx } from 'src/utils/cx'
import {
  isModelEarlyAccess,
  isModelPreviewTest,
  isModelPrivate,
} from 'src/utils/generationModel/modelPermissions'
import { createModelUrl, isModelCoverWarned } from 'src/utils/model'
import { getModelCoverAlt } from 'src/utils/seo'
import { track } from 'src/utils/tracker'

/**
 * the model item display variant, for now it can appear in two places:
 * 1. artwork-detail page, where it shows the models and loras used to generate the artwork
 * 2. model-detail-lora-rec page, where it shows the loras recommended for the model
 */
export const ModelItemDisplayVariant = {
  ArtworkDetail: 'artwork-detail',
  ModelDetailLoraRec: 'model-detail-lora-rec',
} as const

export type ModelItemDisplayVariant = ValueOf<typeof ModelItemDisplayVariant>

export default function ModelItemDisplay(
  props: StyleProps & {
    /**
     * When being undefined, a skeleton will be rendered
     */
    model?: GenerationModelPreviewFragment
    /**
     * Variant for tracking and behavior
     * @default ModelItemDisplayVariant.ArtworkDetail
     */
    variant?: ModelItemDisplayVariant
    /**
     * Callback when the item is clicked
     */
    onClick?: (model: GenerationModelPreviewFragment) => void
  },
) {
  const {
    model,
    variant = ModelItemDisplayVariant.ArtworkDetail,
    onClick,
  } = props
  const trackingScene =
    variant === ModelItemDisplayVariant.ModelDetailLoraRec
      ? 'model-detail-lora-rec'
      : 'artwork-detail'
  const { t } = useTranslation(['market'])
  const hideCoverPreview = isModelCoverWarned(model)

  const isEarlyAccess = isModelEarlyAccess(model)
  const isPrivate = isModelPrivate(model)
  const isPreviewTest = isModelPreviewTest(model)

  const modelUrl = model && !isPrivate ? createModelUrl(model) : undefined

  return (
    <a
      title={model?.title}
      href={modelUrl}
      className={cx(
        'flex items-center gap-2 p-1 -m-1 text-left rounded-xl',
        '[&:not(&:has([data-capture-hover]:hover))]:hover:bg-black/10',
        'dark:[&:not(&:has([data-capture-hover]:hover))]:hover:bg-white/10',
        isPrivate && 'pointer-events-none',
        props.className,
      )}
      style={props.style}
      onClick={event => {
        track('model_click', {
          scene: trackingScene,
          type: model?.type,
          modelId: model?.id,
        })
        event.preventDefault()

        // Call custom onClick callback if provided
        if (onClick && model) {
          onClick(model)
        }

        // For model-detail-lora-rec scene, open model page in a new tab
        if (variant === ModelItemDisplayVariant.ModelDetailLoraRec) {
          if (modelUrl) {
            window.open(modelUrl, '_blank')
          }
          return
        }

        /**
         * `model` is {@link GenerationModelPreviewFragment} — a subset of the full
         * detail fragment. The sheet uses it only as optimistic-UI seed data and
         * immediately re-fetches the full detail by id. Call sites of this component
         * only ever have preview data.
         */
        return sheetModelDetail({ model })
      }}
    >
      {/* Cover */}
      <div className="flex-none grid size-16 overflow-hidden bg-zinc-300 dark:bg-zinc-600 rounded-lg ring-1 ring-black/10 dark:ring-white/10">
        {hideCoverPreview ? (
          <MediaContentRemovedIcon className="row-start-1 col-start-1 place-self-center text-2xl" />
        ) : (
          <>
            <MediaPreview
              thumbnail
              mediaId={model?.mediaId}
              media={model?.media}
              alt={getModelCoverAlt(t, model)}
              className="row-start-1 col-start-1"
            />
            {(isPrivate || isPreviewTest) && (
              <>
                <div className="row-start-1 col-start-1 relative bg-black/70" />
                <IcRoundLockIcon className="row-start-1 col-start-1 place-self-center relative text-xl text-white" />
              </>
            )}
          </>
        )}
      </div>
      <div className="flex-1 overflow-hidden">
        <div className="flex gap-1 items-center">
          {isEarlyAccess && (
            <>
              <MembershipIconCircled />
            </>
          )}
          {/* Type label */}
          {model && <ModelTypeChip model={model} type="compact" />}
          {model && <ModelPreviewEAChip model={model} type="compact" />}
          {!model && (
            <div className="rounded w-[4em] bg-skeleton animate-pulse h-4" />
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Title */}
          {model ? (
            <span className="flex-1 w-full truncate text-sm font-medium">
              {model.title}
            </span>
          ) : (
            <div className="flex-1 rounded w-[4em] bg-skeleton animate-pulse h-5" />
          )}
          {/* Like button */}
          <span
            data-capture-hover=""
            className="flex-none pl-0.5 pr-1 hover:bg-black/10 dark:hover:bg-white/10 rounded"
          >
            {model ? (
              <ModelLikeButton
                modelId={model.id}
                model={model}
                countClassName="text-sm"
                scene={trackingScene}
              />
            ) : (
              <div className="rounded mx-auto w-[3em] bg-skeleton animate-pulse h-6" />
            )}
          </span>
        </div>
      </div>
    </a>
  )
}
